import Grid from '@mui/material/Grid'

import { useMutation } from '@tanstack/react-query'
import { clientsAPI } from 'api/clientsAPI'
import TimelineForm from 'components/UI/TimelineForm'
import BaseLayout from 'layouts/Base/BaseLayout'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Modal,
  Typography,
} from '@mui/material'
import { getInvoiceSettings } from 'api/clientsAPI'
import { platformAPI } from 'api/platformAPI'
import { getUser } from 'api/usersAPI'
import * as yup from 'yup'
import { BillingDetails } from './FormParts/BillingDetails'
import { ClientSettings } from './FormParts/ClientSettingsSimple'
import { CompanyDetails } from './FormParts/CompanyDetails'

// const EmptyFormComponent: React.FC = () => null

const CreateClientPageNew: React.FC<any> = () => {
  const history = useHistory()
  // const dispatch = useDispatch()
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [companyLocation, setCompanyLocation] = useState<string>('GBR')
  const [billedFromParent, setBilledFromParent] = useState<boolean>(false)
  const [formData, setFormData] = useState({})
  const [formBasis, setFormBasis] = useState<any>({})
  const [showError, setShowError] = useState(false)
  const [errorDetail, setErrorDetail] = useState<any>('')
  const [selectedPlatform, setSelectedPlatform] = useState<number>(1)

  useEffect(() => {
    const fetchContext = async () => {
      try {
        const { clientId, roles } = await getUser()
        const { invoiceTypeName } = await getInvoiceSettings(clientId)
        const platform = await platformAPI.getPlatform(selectedPlatform)

        setFormBasis({
          ...formBasis,
          invoiceTypeName,
          isJyrneyAdmin: roles.includes('Jyrney_Admin'),
          clientId,
          platform,
        })
      } catch (err) {
        console.error(err)
      }
    }
    fetchContext().catch((err) => console.log('>>>>>', err))
  }, [selectedPlatform, billedFromParent])

  const newClientStepFields: string[][] = [
    [
      'registrationNumber',
      'parentClientId',
      'settings.isBilledFromParent',
      'validated',
      'companyName',
      'tradingAs',
      'address.addressLine1',
      'address.addressLine2',
      'address.town',
      'address.county',
      'address.postCode',
      'companyLocation',
      'accountManager',
      'contact.name',
      'contact.email',
      'contact.contactNumber',
    ],

    [
      'sameAsCompanyAddress',
      'billing.entity',
      'billing.address.addressLine1',
      'billing.address.addressLine2',
      'billing.address.county',
      'billing.address.town',
      'billing.address.postCode',
      'billing.billingTermTypeName',
      'billing.billingTermValue',
      'billing.vatNumber',
      'billing.vatRegistered',
      'billing.payment.accountHolderName',
      'billing.payment.accountNumber',
      'billing.payment.sortCode',
      'billing.contact.name',
      'billing.contact.email',
      'billing.contact.contactNumber',
    ],
    [
      'settings.applyDestinationLogic',
      'settings.applyBackupVehicleLogic',
      'settings.applyFixedPricing',
      'settings.automaticAllocation',
      'settings.automaticReallocation',
      'settings.isManaged',
      'commission',
      'markup',
    ],
  ]
  const contextFields = {
    step: currentStep,
    companyLocation: companyLocation,
    isBilledFromParent: billedFromParent,
    isPlatformBilling:
      formBasis?.platform?.platformInvoiceTypeName === 'Platform',
    descendantClients: formBasis?.descendantClients,
  }
  const newClientSchema = yup.object().shape({
    registrationNumber: yup
      .string()
      .nullable()
      .transform((val, orig) => (orig === '' ? null : val))
      .when('$companyLocation', {
        is: 'GBR',
        then: (s) =>
          s.test(
            'uk-reg-format',
            'Invalid company number, please check and try again',
            (val) => !val || /^(?:\d{8}|[A-Z]{2}\d{6})$/.test(val),
          ),
        otherwise: (s) => s.notRequired(),
      })
      .notRequired(),
    validated: yup.string().when(['$companyLocation', '$step'], {
      is: (companyLocation: string, step: number) =>
        companyLocation === 'GBR' && step === 0,
      then: (s) => s.required('We need to double check your company number'),
      otherwise: (s) => s.notRequired(),
    }),
    parentClientId: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .nullable()
      .notRequired(),
    companyName: yup.string().when('$step', {
      is: 0,
      then: (s) => s.required('Company name is required'),
      otherwise: (s) => s.notRequired(),
    }),
    companyLocation: yup
      .string()
      .min(1, 'Country is required')
      .when('$step', {
        is: 0,
        then: (s) => s.required('Country is required'),
        otherwise: (s) => s.notRequired(),
      }),
    contact: yup.object().shape({
      name: yup.string().when('$step', {
        is: 0,
        then: (s) => s.required('Company Contact Name is required'),
        otherwise: (s) => s.notRequired(),
      }),
      email: yup
        .string()
        .matches(
          /^[\w-.]+@([\w-]+.)+[\w-]{2,4}$/,
          'Enter a valid email address',
        )
        .when('$step', {
          is: 0,
          then: (s) => s.required('Company Contact Email Address is required'),
          otherwise: (s) => s.notRequired(),
        }),
      contactNumber: yup
        .string()
        .when('$step', {
          is: 0,
          then: (s) => s.required('Company Contact Phone Number is required'),
          otherwise: (s) => s.notRequired(),
        })
        .matches(/^\+[1-9]\d{1,14}$/, 'Please enter a valid phone number'),
    }),
    address: yup
      .object()
      .shape({
        addressLine1: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('Street Address is required'),
          otherwise: (s) => s.notRequired(),
        }),
        town: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('Town / City is required'),
          otherwise: (s) => s.notRequired(),
        }),
        county: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('County is required'),
          otherwise: (s) => s.notRequired(),
        }),
        postCode: yup.string().when(['$companyLocation', '$step'], {
          is: (companyLocation: string, step: number) =>
            companyLocation === 'GBR' && step === 0,
          then: (s) =>
            s
              .matches(
                /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$/,
                'Invalid UK postcode format',
              )
              .required(
                companyLocation === 'GBR'
                  ? 'Post code is required'
                  : 'Postal / Zip code is required',
              ),
          otherwise: (s) => s.required('Postal / Zip code is required'),
        }),
        country: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('Country is required'),
          otherwise: (s) => s.notRequired(),
        }),
        countryCode: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('Country is required'),
          otherwise: (s) => s.notRequired(),
        }),
      })
      .required(),
    accountManager: yup
      .number()
      .min(1, 'Please choose an account manager')
      .when('$step', {
        is: 0,
        then: (s) => s.required('Please choose an account manager'),
        otherwise: (s) => s.notRequired(),
      }),
    sameAsCompanyAddress: yup.boolean(),
    billing: yup.lazy((_value, { context }) =>
      context?.isPlatformBilling
        ? yup.mixed().notRequired().nullable().strip()
        : yup
            .object()
            .nullable()
            .shape({
              entity: yup.string().when(['$isBilledFromParent', '$step'], {
                is: (isBilledFromParent: boolean, step: number) =>
                  !isBilledFromParent && step === 1,
                then: (s) => s.required('Billing Entity is required'),
                otherwise: (s) => s.notRequired(),
              }),
              contact: yup.object().shape({
                name: yup.string().when(['$isBilledFromParent', '$step'], {
                  is: (isBilledFromParent: boolean, step: number) =>
                    !isBilledFromParent && step === 1,
                  then: (s) => s.required('Contact name required'),
                  otherwise: (s) => s.notRequired(),
                }),
                email: yup
                  .string()
                  .matches(
                    /^[\w-.]+@([\w-]+.)+[\w-]{2,4}$/,
                    'Enter a valid email address',
                  )
                  .when(['$isBilledFromParent', '$step'], {
                    is: (isBilledFromParent: boolean, step: number) =>
                      !isBilledFromParent && step === 1,
                    then: (s) => s.required('Contact email required'),
                    otherwise: (s) => s.notRequired(),
                  }),
                contactNumber: yup
                  .string()
                  .matches(
                    /^\+[1-9]\d{1,14}$/,
                    'Please enter a valid phone number',
                  )
                  .when(['$isBilledFromParent', '$step'], {
                    is: (isBilledFromParent: boolean, step: number) =>
                      !isBilledFromParent && step === 1,
                    then: (s) => s.required('Contact number required'),
                    otherwise: (s) => s.notRequired(),
                  }),
              }),
              address: yup.object().shape({
                addressLine1: yup
                  .string()
                  .when(['$isBilledFromParent', '$step'], {
                    is: (isBilledFromParent: boolean, step: number) =>
                      !isBilledFromParent && step === 1,
                    then: (s) => s.required('Street Address is required'),
                    otherwise: (s) => s.notRequired(),
                  }),
                town: yup.string().when(['$isBilledFromParent', '$step'], {
                  is: (isBilledFromParent: boolean, step: number) =>
                    !isBilledFromParent && step === 1,
                  then: (s) => s.required('Town / City is required'),
                  otherwise: (s) => s.notRequired(),
                }),
                postCode: yup
                  .string()
                  .when(['$isBilledFromParent', '$companyLocation', '$step'], {
                    is: (
                      isBilledFromParent: boolean,
                      companyLocation: string,
                      step: number,
                    ) =>
                      !isBilledFromParent &&
                      companyLocation === 'GBR' &&
                      step === 1,
                    then: (s) =>
                      s
                        .matches(
                          /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$/,
                          'Invalid UK postcode format',
                        )
                        .required('Post code is required'),
                    otherwise: (s) => s.notRequired(),
                  }),
                country: yup.string().when(['$isBilledFromParent', '$step'], {
                  is: (isBilledFromParent: boolean, step: number) =>
                    !isBilledFromParent && step === 1,
                  then: (s) => s.required('Country is required'),
                  otherwise: (s) => s.notRequired(),
                }),
                countryCode: yup
                  .string()
                  .when(['$isBilledFromParent', '$step'], {
                    is: (isBilledFromParent: boolean, step: number) =>
                      !isBilledFromParent && step === 1,
                    then: (s) => s.required('Country is required'),
                    otherwise: (s) => s.notRequired(),
                  }),
              }),
              billingTermTypeName: yup
                .string()
                .when(['$isBilledFromParent', '$step'], {
                  is: (isBilledFromParent: boolean, step: number) =>
                    !isBilledFromParent && step === 1,
                  then: (s) => s.required('Choose a term'),
                  otherwise: (s) => s.notRequired(),
                }),
              billingTermValue: yup
                .number()
                .typeError('Billing term is invalid')
                .min(7, 'Minimum billing term is 7 days')
                .when(
                  [
                    '$isBilledFromParent',
                    '$step',
                    'billing.billingTermTypeName',
                  ],
                  {
                    is: (
                      isBilledFromParent: boolean,
                      step: number,
                      type: string,
                    ) =>
                      !isBilledFromParent &&
                      step === 1 &&
                      type === 'DaysFromInvoice',
                    then: (s) => s.required('Days are required').max(31),
                    otherwise: (s) => s.notRequired(),
                  },
                ),
              vatNumber: yup
                .string()
                .when(['$isBilledFromParent', '$companyLocation', '$step'], {
                  is: (isBilledFromParent: boolean, step: number) =>
                    !isBilledFromParent && step === 1,
                  then: (s) => s.required('VAT Number Required'),
                  otherwise: (s) => s.notRequired(),
                }),
              payment: yup.object().shape({
                accountHolderName: yup
                  .string()
                  .when(['$isBilledFromParent', '$step'], {
                    is: (isBilledFromParent: boolean, step: number) =>
                      !isBilledFromParent && step === 1,
                    then: (s) =>
                      s.required('Account Holder or Business Name required'),
                    otherwise: (s) => s.notRequired(),
                  }),
                sortCode: yup.string().when(['$isBilledFromParent', '$step'], {
                  is: (isBilledFromParent: boolean, step: number) =>
                    !isBilledFromParent && step === 1,
                  then: (s) =>
                    s
                      .matches(/^[\d]{6}$/, 'Invalid Sort code')
                      .required('Sort code is required'),
                  otherwise: (s) => s.notRequired(),
                }),
                accountNumber: yup
                  .string()
                  .when(['$isBilledFromParent', '$step'], {
                    is: (isBilledFromParent: boolean, step: number) =>
                      !isBilledFromParent && step === 1,
                    then: (s) =>
                      s
                        .matches(/^[\d]{8}$/, 'Invalid Account Number')
                        .required('Account Number is required'),
                    otherwise: (s) => s.notRequired(),
                  }),
              }),
            })
            .notRequired()
            .nullable()
            .default(undefined),
    ),
  })

  const formsData = [
    {
      formName: 'Client Details',
      expanded: true,
      formObject: CompanyDetails,
      shouldRender: true,
    },
    {
      formName: 'Billing Details',
      expanded: false,
      formObject: BillingDetails,
      shouldRender:
        !contextFields.isPlatformBilling && !contextFields.isBilledFromParent,
    },
    {
      formName: 'Default Settings',
      expanded: false,
      formObject: ClientSettings,
      shouldRender: true,
    },
  ]

  const defaultNewClientValues = {
    validated: false,
    platformId: 1,
    status: 'Onboarding',
    companyLocation: 'GBR',
    parentClientId: null,
    registrationNumber: '',
    companyName: '',
    commission: 0,
    markup: 0,
    accountManager: 498,
    address: {
      addressLine1: '',
      addressLine2: '',
      town: '',
      county: '',
      postCode: '',
      country: 'United Kingdom',
      countryCode: 'GBR',
    },
    billing: {
      vatRegistered: false,
    },
    currency: '',
    clientTypeName: 'Corporate',
    timezone: 'Europe/London',
    settings: {
      applyDestinationLogic: false,
      applyBackupVehicleLogic: false,
      applyFixedPricing: false,
      automaticAllocation: false,
      automaticReallocation: false,
      isManaged: false,
      isBilledFromParent: false,
    },
  }

  const submitData = useMutation({
    mutationFn: async (formData: any) => {
      if (contextFields.isBilledFromParent || contextFields.isPlatformBilling) {
        delete formData.billing
      }
      return await clientsAPI.createClient(formData)
    },
    onSuccess: async (data) => {
      if (data.data.id) {
        history.push(`/admin/clients/${data.data.id}`)
      }
    },
    onError: async (error) => {
      setErrorDetail(JSON.stringify(error, null, 2))
      setShowError(true)
    },
  })

  useEffect(() => {
    console.log('selectedPlatform', selectedPlatform)
  }, [selectedPlatform])

  return (
    <BaseLayout
      pageTitle="Create Client"
      pageSubtitle="Enter new Client information"
      mainElement={
        <Grid
          container
          justifyContent="center"
          sx={{ display: 'flex', flexDirection: 'row' }}
        >
          <Grid
            item
            xs={12}
            xl={8}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <TimelineForm
              formParts={formsData}
              submitData={submitData}
              defaultValues={defaultNewClientValues}
              formSchema={newClientSchema}
              stepFields={newClientStepFields}
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
              companyLocation={companyLocation}
              setCompanyLocation={setCompanyLocation}
              formData={formData}
              setFormData={setFormData}
              contextFields={contextFields}
              setBilledFromParent={setBilledFromParent}
              selectedPlatform={selectedPlatform}
              setSelectedPlatform={setSelectedPlatform}
            />
          </Grid>
          <Modal
            open={showError}
            onClose={() => {
              setShowError(false)
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'auto',
                maxWidth: '70dvw',
                bgcolor: 'background.paper',
                boxShadow: 24,
                borderRadius: '4px',
                p: 4,
              }}
            >
              <Typography id="error-title" variant="h6" component="h2">
                Sorry, there was an issue
              </Typography>
              <Typography id="error-description" sx={{ mt: 2 }}>
                We were unable to create this client for you, due to an
                unexpected error.
              </Typography>
              <Typography id="error-description" sx={{ mt: 2 }}>
                Please get in touch with{' '}
                <span style={{ fontWeight: 500 }}>
                  clientservices@jyrney.com
                </span>{' '}
                to report this issue.
              </Typography>

              {errorDetail.length > 0 && (
                <Accordion sx={{ marginTop: '15px' }}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="error-details"
                    id="error-details"
                  >
                    <code style={{ color: 'blue' }}>Error Details</code>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      component="pre"
                      id="error-details"
                      sx={{ mt: 2 }}
                    >
                      <code>{errorDetail}</code>
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          </Modal>
          {/* {validateAccess('Jyrney_Admin') && (
            <Grid
              item
              xs={12}
              xl={3}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <ClientHierarchyTree />
            </Grid>
          )} */}
        </Grid>
      }
    />
  )
}

export default CreateClientPageNew
